import { GQBiEmptyStringType, GQFlags } from '../generated/graphql';
import {
  BUSINESS_CATEGORY_LABELS,
  GHG_CATEGORY_LABELS,
} from '../utils/BusinessCategory';
import { BiDimensionFieldMeta, BiFieldType, BiMeasureFieldMeta } from './types';
import IntensityDenominators, {
  IntensityDenominatorKinds,
} from '../utils/intensityDenominators';
import { countries } from '../countryConstants';
import { UNIT_LABELS } from '../utils/unitLabels';
import { BART_LEASE_TYPE_LABELS } from '../utils/buildingLeaseTypeLabels';
import { z } from 'zod';
import {
  CAS_NUMBER_LABELS,
  MICROPLASTICS_OUTFLOW_MEDIUM_LABELS,
  POLLUTANT_GROUP_LABELS,
  SUBSTANCE_AND_MICROPLASTICS_INFLOW_MEDIUM_LABELS,
  SUBSTANCE_OF_CONCERN_LABELS,
  SUBSTANCE_OUTFLOW_MEDIUM_LABELS,
} from '../canonicalSchemas/constants';
export const BIODIVERSITY_FAMILY = 'Biodiversity';
export const BUILDING_FAMILY = 'Building';
export const CATEGORY_FAMILY = 'Watershed categorization';
export const SUPPLIER_FAMILY = 'Suppliers & Products';
export const LOCATION_FAMILY = 'Location';
export const GHG_FAMILY = 'GHG Protocol';
export const WASTE_FAMILY = 'Waste';
export const POLLUTION_FAMILY = 'Pollution';
export const WATER_FAMILY = 'Water';
export const FINANCIALS_FAMILY = 'Financials';
export const OTHER_FAMILY = 'Other';
export const TIME_FAMILY = 'Time';
export const ENERGY_FAMILY = 'Energy';
export const PRODUCT_CIRCULARITY_FAMILY = 'Product circularity';
export const INTERNAL_FAMILY = 'Internal';

const DIMENSION_META_DEFAULTS = {
  type: BiFieldType.string,
  fieldFamily: undefined,
  shouldSentenceCaseValues: true,
  shouldNullifyPseudoNulls: true,
  // all our current usages of dimensinos want this behavior so default to it for dimensions only
  // we can override to Empty string instead if say we display the dimension value in a flat column rather than a tree
  emptyStringType: GQBiEmptyStringType.UnspecifiedWithDisplayName,
  isMultiValue: false,
};
const MEASURE_META_DEFAULTS = {
  type: BiFieldType.number,
  emptyStringType: GQBiEmptyStringType.Emdash,
};

const legacyIntensities: Record<
  string,
  Omit<BiMeasureFieldMeta, 'fieldId'>
> = Object.fromEntries(
  IntensityDenominatorKinds.map((intensityKind) => [
    intensityKind,
    measureMeta({
      displayName: IntensityDenominators[intensityKind].label.replace(
        ' intensity',
        ''
      ),
      divideBy: IntensityDenominators[intensityKind].scaleFactor,
      unitUserFacing: IntensityDenominators[intensityKind].unitUserFacing,
      isCurrency: IntensityDenominators[intensityKind].isCurrency,
    }),
  ])
);

const recordToStaticListOptions = (record: Record<string, string>) =>
  Object.entries(record).map(([value, label]) => ({ value, label }));

/**
 * Assumes the following defaults:
 * type: string
 */
export const BI_DIMENSION_FIELDS: Record<
  string,
  Omit<BiDimensionFieldMeta, 'fieldId'>
> = {
  month: dimensionMeta({
    displayName: 'Month',
    type: BiFieldType.date,
    hiddenInFilters: true,
    fieldFamily: TIME_FAMILY,
    format: {
      month: 'short',
      year: 'numeric',
    },
  }),
  calendarYear: dimensionMeta({
    displayName: 'Calendar year',
    type: BiFieldType.number,
    hiddenInFilters: true,
    disableCommas: true,
    fieldFamily: TIME_FAMILY,
  }),
  fiscalYear: dimensionMeta({
    displayName: 'Fiscal year',
    type: BiFieldType.number,
    hiddenInFilters: true,
    disableCommas: true,
    fieldFamily: TIME_FAMILY,
  }),
  product: dimensionMeta({
    displayName: 'Product',
    fieldFamily: SUPPLIER_FAMILY,
  }),
  uncollapsedCompanyId: dimensionMeta({
    displayName: 'Supplier - Standardized',
    fieldFamily: SUPPLIER_FAMILY,
  }),
  vendor: dimensionMeta({
    displayName: 'Supplier',
    fieldFamily: SUPPLIER_FAMILY,
  }),
  rawVendor: dimensionMeta({
    displayName: 'Supplier - Imported',
    fieldFamily: SUPPLIER_FAMILY,
  }),
  locationCountry: dimensionMeta({
    displayName: 'Country',
    shouldSentenceCaseValues: false,
    staticListOptions: countries.map((c) => ({
      value: c['alpha-2'],
      label: c.name,
    })),
    fieldFamily: LOCATION_FAMILY,
  }),
  locationCity: dimensionMeta({
    displayName: 'City',
    shouldSentenceCaseValues: false,
    fieldFamily: LOCATION_FAMILY,
  }),
  locationState: dimensionMeta({
    displayName: 'State',
    shouldSentenceCaseValues: false,
    fieldFamily: LOCATION_FAMILY,
  }),
  location: dimensionMeta({
    displayName: LOCATION_FAMILY,
    shouldSentenceCaseValues: false,
    fieldFamily: LOCATION_FAMILY,
  }),
  categoryId: dimensionMeta({
    displayName: 'Category',
    staticListOptions: recordToStaticListOptions(BUSINESS_CATEGORY_LABELS),
    fieldFamily: CATEGORY_FAMILY,
  }),
  subcategoryId: dimensionMeta({
    displayName: 'Subcategory',
    fieldFamily: CATEGORY_FAMILY,
  }),
  description: dimensionMeta({
    displayName: 'Description',
    fieldFamily: CATEGORY_FAMILY,
  }),
  ghgCategoryId: dimensionMeta({
    displayName: 'GHG category',
    staticListOptions: recordToStaticListOptions(GHG_CATEGORY_LABELS),
    fieldFamily: GHG_FAMILY,
  }),
  ghgScope: dimensionMeta({
    displayName: 'GHG scope',
    shouldSentenceCaseValues: false,
    fieldFamily: GHG_FAMILY,
  }),
  sourceUnit: dimensionMeta({
    displayName: 'Source unit',
    shouldSentenceCaseValues: false,
    staticListOptions: recordToStaticListOptions(UNIT_LABELS),
  }),
  inputUnit: dimensionMeta({
    displayName: 'Source unit',
    shouldSentenceCaseValues: false,
    staticListOptions: recordToStaticListOptions(UNIT_LABELS),
  }),
  isSupplierSpecific: dimensionMeta({
    displayName: 'Is supplier specific',
  }),
  isCustomerProvidedEmissionsFactor: dimensionMeta({
    displayName: 'Is customer-provided emissions factor',
    type: BiFieldType.boolean,
  }),
  hasKwhConsumed: dimensionMeta({
    displayName: 'Has energy consumption',
    type: BiFieldType.boolean,
    fieldFamily: ENERGY_FAMILY,
  }),
  fuelKind: dimensionMeta({
    displayName: 'Fuel kind',
    fieldFamily: ENERGY_FAMILY,
  }),
  energyKind: dimensionMeta({
    displayName: 'Energy kind',
    fieldFamily: ENERGY_FAMILY,
  }),
  isRenewable: dimensionMeta({
    displayName: 'Is renewable',
    type: BiFieldType.boolean,
    fieldFamily: ENERGY_FAMILY,
  }),
  isOnsite: dimensionMeta({
    displayName: 'Is onsite',
    type: BiFieldType.boolean,
    fieldFamily: ENERGY_FAMILY,
  }),
  isCogeneration: dimensionMeta({
    displayName: 'Is co-generation',
    type: BiFieldType.boolean,
    fieldFamily: ENERGY_FAMILY,
  }),
  fuelCombustionReason: dimensionMeta({
    displayName: 'Fuel combustion reason',
    fieldFamily: ENERGY_FAMILY,
  }),
  heatingValueKind: dimensionMeta({
    displayName: 'Heating value kind',
    fieldFamily: ENERGY_FAMILY,
  }),
  buildingName: dimensionMeta({
    displayName: 'Building identifier',
    shouldSentenceCaseValues: false,
    fieldFamily: BUILDING_FAMILY,
  }),
  buildingKind: dimensionMeta({
    displayName: 'Building kind',
    fieldFamily: BUILDING_FAMILY,
  }),
  buildingLeaseType: dimensionMeta({
    displayName: 'Building lease type',
    shouldSentenceCaseValues: false,
    staticListOptions: recordToStaticListOptions(BART_LEASE_TYPE_LABELS),
    fieldFamily: BUILDING_FAMILY,
  }),
  measurementProjectIds: dimensionMeta({
    displayName: 'Measurement project',
    isMultiValue: true,
  }),
  fileIds: dimensionMeta({
    displayName: 'Files',
    isMultiValue: true,
  }),
  isBuildingEmpty: dimensionMeta({
    displayName: 'Is building empty',
    type: BiFieldType.boolean,
    fieldFamily: BUILDING_FAMILY,
  }),
  dataOwners: dimensionMeta({
    displayName: 'Data owners',
    isMultiValue: true,
    shouldSentenceCaseValues: false,
  }),
  wasteTreatment: dimensionMeta({
    displayName: 'Waste treatment',
    fieldFamily: WASTE_FAMILY,
  }),
  wasteMaterial: dimensionMeta({
    displayName: 'Waste material',
    fieldFamily: WASTE_FAMILY,
  }),
  wasteLifecycle: dimensionMeta({
    displayName: 'Waste lifecycle',
    fieldFamily: WASTE_FAMILY,
  }),
  isWasteHazardous: dimensionMeta({
    displayName: 'Waste is hazardous',
    fieldFamily: WASTE_FAMILY,
    type: BiFieldType.boolean,
  }),
  isWasteRadioactive: dimensionMeta({
    displayName: 'Waste is radioactive',
    type: BiFieldType.boolean,
    fieldFamily: WASTE_FAMILY,
  }),
  areaUnit: dimensionMeta({
    displayName: 'Area unit',
  }),
  volumeUnit: dimensionMeta({
    displayName: 'Volume unit',
  }),
  massUnit: dimensionMeta({
    displayName: 'Mass unit',
  }),
  energyUnit: dimensionMeta({
    displayName: 'Energy unit',
  }),
  fuelUnit: dimensionMeta({
    displayName: 'Fuel unit',
  }),
  aggregatedFuelUnit: dimensionMeta({
    displayName: 'Aggregated fuel unit',
  }),
  casNumber: dimensionMeta({
    displayName: 'Pollutant CAS number',
    fieldFamily: POLLUTION_FAMILY,
    staticListOptions: recordToStaticListOptions(CAS_NUMBER_LABELS),
  }),
  euPollutantReleaseAndTransferRegisterPollutantGroup: dimensionMeta({
    displayName: 'EU Pollutant Release and Transfer Register pollutant group',
    fieldFamily: POLLUTION_FAMILY,
    staticListOptions: recordToStaticListOptions(POLLUTANT_GROUP_LABELS),
  }),
  pollutantMedium: dimensionMeta({
    displayName: 'Pollutant medium',
    fieldFamily: POLLUTION_FAMILY,
  }),
  pollutantSubstanceOfConcern: dimensionMeta({
    displayName: 'Substance of concern',
    fieldFamily: POLLUTION_FAMILY,
    staticListOptions: recordToStaticListOptions(SUBSTANCE_OF_CONCERN_LABELS),
  }),
  pollutantSubstanceOfConcernHazardClass: dimensionMeta({
    displayName: 'Substance of concern hazard class',
    fieldFamily: POLLUTION_FAMILY,
    isMultiValue: true,
  }),
  substanceInflowMedium: dimensionMeta({
    displayName: 'Substance inflow medium',
    staticListOptions: recordToStaticListOptions(
      SUBSTANCE_AND_MICROPLASTICS_INFLOW_MEDIUM_LABELS
    ),
  }),
  substanceOutflowMedium: dimensionMeta({
    displayName: 'Substance outflow medium',
    staticListOptions: recordToStaticListOptions(
      SUBSTANCE_OUTFLOW_MEDIUM_LABELS
    ),
  }),
  microplasticsInflowMedium: dimensionMeta({
    displayName: 'Microplastics inflow medium',
    staticListOptions: recordToStaticListOptions(
      SUBSTANCE_AND_MICROPLASTICS_INFLOW_MEDIUM_LABELS
    ),
  }),
  microplasticsOutflowMedium: dimensionMeta({
    displayName: 'Microplastics outflow medium',
    staticListOptions: recordToStaticListOptions(
      MICROPLASTICS_OUTFLOW_MEDIUM_LABELS
    ),
  }),
  financialAccountId: dimensionMeta({
    displayName: 'Financial account ID',
    shouldSentenceCaseValues: false,
    fieldFamily: FINANCIALS_FAMILY,
  }),
  financialAccountName: dimensionMeta({
    displayName: 'Financial account name',
    shouldSentenceCaseValues: false,
    fieldFamily: FINANCIALS_FAMILY,
  }),
  uploaderIds: dimensionMeta({
    displayName: 'Uploader',
    isMultiValue: true,
  }),
  datasetId: dimensionMeta({
    displayName: 'Dataset',
  }),
  beaCode: dimensionMeta({
    displayName: 'BEA code',
    shouldSentenceCaseValues: false,
    fieldFamily: FINANCIALS_FAMILY,
  }),
  electricityType: dimensionMeta({
    displayName: 'Electricity type',
  }),
  material: dimensionMeta({
    displayName: 'Material',
  }),
  releaseId: dimensionMeta({
    displayName: 'Calculation method',
    gatingFeatureFlag: GQFlags.CalculationEnableReleaseSelection,
  }),

  /**
   * BEGIN Chanel demo fields
   * TODO(ishaan): region, gender, division, and batMatcherFilter are all for
   * the Chanel demo and should be removed once the demo is no longer needed.
   */
  gender: dimensionMeta({
    displayName: 'Gender',
    shouldSentenceCaseValues: false,
  }),
  region: dimensionMeta({
    displayName: 'Region',
    shouldSentenceCaseValues: false,
  }),
  division: dimensionMeta({
    displayName: 'Division',
    shouldSentenceCaseValues: false,
  }),
  /**
   * END Chanel demo fields
   */

  batMatcherFilter: dimensionMeta({
    displayName: 'BAT matcher filter',
    shouldSentenceCaseValues: false,
  }),
  'aqueduct40.waterStressLabel': dimensionMeta({
    displayName: 'Water stress',
    fieldFamily: WATER_FAMILY,
    type: BiFieldType.string,
  }),
  'aqueduct40.waterDepletionLabel': dimensionMeta({
    displayName: 'Water depletion',
    fieldFamily: WATER_FAMILY,
    type: BiFieldType.string,
  }),

  /**
   * BEGIN product circularity fields
   */
  productName: dimensionMeta({
    displayName: 'Product name',
    fieldFamily: PRODUCT_CIRCULARITY_FAMILY,
    type: BiFieldType.string,
    shouldSentenceCaseValues: false,
    gatingFeatureFlag: GQFlags.BiDrilldownProductCircularityMeasures,
  }),
  durability: dimensionMeta({
    displayName: 'Product durability',
    fieldFamily: PRODUCT_CIRCULARITY_FAMILY,
    type: BiFieldType.string,
    shouldSentenceCaseValues: false,
    gatingFeatureFlag: GQFlags.BiDrilldownProductCircularityMeasures,
  }),
  industryAverageDurability: dimensionMeta({
    displayName: 'Product durability industry average',
    fieldFamily: PRODUCT_CIRCULARITY_FAMILY,
    type: BiFieldType.string,
    shouldSentenceCaseValues: false,
    gatingFeatureFlag: GQFlags.BiDrilldownProductCircularityMeasures,
  }),
  repairability: dimensionMeta({
    displayName: 'Product repairability',
    fieldFamily: PRODUCT_CIRCULARITY_FAMILY,
    type: BiFieldType.string,
    shouldSentenceCaseValues: false,
    gatingFeatureFlag: GQFlags.BiDrilldownProductCircularityMeasures,
  }),
  repairabilityRatingSystem: dimensionMeta({
    displayName: 'Product repairability rating system',
    fieldFamily: PRODUCT_CIRCULARITY_FAMILY,
    type: BiFieldType.string,
    shouldSentenceCaseValues: false,
    gatingFeatureFlag: GQFlags.BiDrilldownProductCircularityMeasures,
  }),
  /**
   * END product circularity field
   */

  /**
   * BEGIN Biodiversity fields
   */
  siteIdentifier: dimensionMeta({
    displayName: 'Site identifier',
    fieldFamily: BIODIVERSITY_FAMILY,
    type: BiFieldType.string,
    shouldSentenceCaseValues: false,
    gatingFeatureFlag: GQFlags.BiDrilldownBiodiversityMeasures,
  }),
  isInOrNearBiodiversitySensitiveArea: dimensionMeta({
    displayName: 'Is in or near a biodiversity sensitive area',
    fieldFamily: BIODIVERSITY_FAMILY,
    type: BiFieldType.boolean,
    gatingFeatureFlag: GQFlags.BiDrilldownBiodiversityMeasures,
  }),
  /**
   * END Biodiversity fields
   */

  /**
   * BEGIN Internal recat fields.
   * These should have the same metadata as the corresponding external fields.
   */
  internalCategoryId: dimensionMeta({
    displayName: 'Category',
    hiddenInGroupBy: true,
    hiddenInFilters: true,
    staticListOptions: recordToStaticListOptions(BUSINESS_CATEGORY_LABELS),
    fieldFamily: CATEGORY_FAMILY,
  }),
  internalSubcategoryId: dimensionMeta({
    displayName: 'Subcategory',
    hiddenInGroupBy: true,
    hiddenInFilters: true,
    fieldFamily: CATEGORY_FAMILY,
  }),
  internalDescription: dimensionMeta({
    displayName: 'Description',
    hiddenInGroupBy: true,
    hiddenInFilters: true,
    fieldFamily: CATEGORY_FAMILY,
  }),
  internalGhgCategoryId: dimensionMeta({
    displayName: 'GHG category',
    hiddenInGroupBy: true,
    hiddenInFilters: true,
    staticListOptions: recordToStaticListOptions(GHG_CATEGORY_LABELS),
    fieldFamily: CATEGORY_FAMILY,
  }),
  /**
   * END Internal recat fields
   */
};

export const BiChartTimeDimensions = z.enum(['month', 'calendarYear']);
export type BiChartTimeDimensionType = z.infer<typeof BiChartTimeDimensions>;
export const BiChartTimeGranularity = z.enum(['month', 'year']);

export type BiChartTimeDimensionMeta = {
  timeGranularity: z.infer<typeof BiChartTimeGranularity>;
};

export const BI_SUPPORTED_TIME_DIMENSIONS: Record<
  BiChartTimeDimensionType,
  BiChartTimeDimensionMeta
> = {
  month: {
    timeGranularity: 'month',
  },
  calendarYear: {
    timeGranularity: 'year',
  },
};

export function isValidBiTimeDimension(
  dim: string
): dim is BiChartTimeDimensionType {
  return Object.values(BiChartTimeDimensions.Values).includes(
    dim as BiChartTimeDimensionType
  );
}

export const BI_MEASURE_FIELDS: Record<
  string,
  Omit<BiMeasureFieldMeta, 'fieldId'>
> = {
  kgco2eQuantity: measureMeta({
    displayName: 'Emissions: market-based',
  }),
  kgco2eLocationQuantity: measureMeta({
    displayName: 'Emissions: location-based',
  }),
  originalTco2eQuantity: measureMeta({
    displayName: 'Original Emissions: market-based',
  }),
  originalMwhConsumed: measureMeta({
    displayName: 'Original energy consumption',
  }),
  tco2eQuantity: measureMeta({
    displayName: 'Emissions: market-based',
  }),
  tco2eLocationQuantity: measureMeta({
    displayName: 'Emissions: location-based',
  }),
  mwhConsumed: measureMeta({
    displayName: 'Energy consumption',
  }),
  kgWasteQuantity: measureMeta({
    displayName: WASTE_FAMILY,
  }),
  kgMicroplasticsInflowQuantity: measureMeta({
    displayName: 'Microplastics inflow',
  }),
  kgMicroplasticsOutflowQuantity: measureMeta({
    displayName: 'Microplastics outflow',
  }),
  kgSubstanceInflowQuantity: measureMeta({
    displayName: 'Pollution inflow',
  }),
  kgSubstanceOutflowQuantity: measureMeta({
    displayName: 'Pollution outflow',
  }),
  buildingAreaQuantity: measureMeta({
    displayName: 'Building area',
  }),
  // FP-5204: This is a separate measure because we want the main measure
  // to be sq ft but we want the intensity measure to be able to use thousands
  // of square feet. Someday, with custom measure intensities and specifying units
  // we can hopefully get rid of this.
  buildingAreaScaledQuantity: measureMeta({
    displayName: 'Building area',
    divideBy: 1000,
    unit: 'square_feet',
    unitUserFacing: '1,000 sq ft',
  }),
  fuelQuantity: measureMeta({
    displayName: 'Fuel consumption',
  }),
  electricityQuantity: measureMeta({
    displayName: 'Electricity consumption',
  }),
  wasteQuantity: measureMeta({
    displayName: 'Waste quantity',
  }),
  employeeQuantity: measureMeta({
    displayName: 'Employees',
  }),
  waterRecyclingQuantity: measureMeta({
    displayName: 'Water recycled volume',
  }),
  waterUsageQuantity: measureMeta({
    displayName: 'Water usage volume',
  }),
  waterStorageQuantity: measureMeta({
    displayName: 'Water storage volume',
  }),
  waterConsumptionQuantity: measureMeta({
    displayName: 'Water consumption volume',
  }),
  waterDischargeQuantity: measureMeta({
    displayName: 'Water discharge volume',
  }),
  litersWaterUsedQuantity: measureMeta({
    displayName: 'Water usage volume',
  }),
  litersWaterConsumedQuantity: measureMeta({
    displayName: 'Water consumption volume',
  }),
  litersWaterStoredQuantity: measureMeta({
    displayName: 'Water storage volume',
  }),
  litersWaterRecycledQuantity: measureMeta({
    displayName: 'Water recycling volume',
  }),
  litersWaterDischargedQuantity: measureMeta({
    displayName: 'Water discharge volume',
  }),
  materialsQuantity: measureMeta({
    displayName: 'Material weight',
  }),
  productQuantity: measureMeta({
    displayName: 'Product',
  }),
  /**
   * TODO(ishaan): employeeWithWageQuantity, averageTrainingHours, and averageHourlyWage are all for
   * the Chanel demo and should be removed once the demo is no longer needed.
   */
  employeeWithWageQuantity: measureMeta({
    displayName: 'Employees',
  }),
  averageTrainingHours: measureMeta({
    displayName: 'Average training hours',
  }),
  averageHourlyWage: measureMeta({
    displayName: 'Average hourly wage',
  }),
  currencyQuantity: measureMeta({
    displayName: 'Spend',
  }),
  siteAreaQuantity: measureMeta({
    displayName: 'Site area',
  }),
  ...legacyIntensities,
};

export function getDimensionMeta(dimension: string): BiDimensionFieldMeta {
  if (BI_DIMENSION_FIELDS[dimension]) {
    return {
      fieldId: dimension,
      ...BI_DIMENSION_FIELDS[dimension],
    };
  } else {
    return {
      ...DIMENSION_META_DEFAULTS,
      fieldId: dimension,
      displayName: dimension,
      fieldFamily: OTHER_FAMILY,
    };
  }
}

export function getMeasureMeta(measure: string): BiMeasureFieldMeta {
  return Object.assign(
    {
      fieldId: measure,
    },
    BI_MEASURE_FIELDS[measure] ?? MEASURE_META_DEFAULTS
  );
}

export function getCustomDimensionMeta({
  fieldId,
  description,
  type,
  displayName,
}: Pick<
  BiDimensionFieldMeta,
  'fieldId' | 'description' | 'type' | 'displayName'
>): BiDimensionFieldMeta {
  return {
    ...DIMENSION_META_DEFAULTS,
    fieldId,
    displayName,
    fieldFamily: 'Custom',
    type,
    description,
    isCustomField: true,
  };
}

function dimensionMeta(
  meta: Partial<BiDimensionFieldMeta> & { displayName: string }
): Omit<BiDimensionFieldMeta, 'fieldId'> {
  return { ...DIMENSION_META_DEFAULTS, ...meta };
}

function measureMeta(
  meta: Partial<BiMeasureFieldMeta> & { displayName: string }
): Omit<BiMeasureFieldMeta, 'fieldId'> {
  return { ...MEASURE_META_DEFAULTS, ...meta };
}
